/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'com/ibm/vida/vizbundles/extensions/decorations',
	'underscore'
], function(decorations, _) {
	'use strict';

	const TEXT_DECORATION = {
		name: 'text',
		backgroundColor: { r: 229, g: 229, b: 229, a: 1 },
		font: null // Need to set to null otherwise it will override the current font size
	};
	// implements IDecorated
	class VIPRDecoratable {
		constructor(object, context) {
			this.context = context;
			//Save indexes that help describes the results data location of the decoratable object.
			//(eg. A VIPRItemClass will have the dataitem index defined but not the tuple (since it corresponds to an axis title)
			//	   A VIPRTuple will have dataitem and tuple defined (since it corresponds to the nth intersected value)
			// 	   A VIPRItem will have dataitem, tuple and tupleitem (since it corresponds to an item in a slot)
			this.indexes = (context && context.index) &&
				{
					dataitem: context.index.dataitem,	//The resultsDataItem index (corresponds to the slot index)
					tuple: context.index.tuple,			//The tuple (corresponds to the nth value eg: 2005-Camping)
					tupleitem: context.index.tupleitem	//The tupleitem is the index (n) for the nth item assigned to the slot
				} || {};
			this.object = object;
			this._initDecoratations();
		}

		_initDecoratations() {
			let decos = this.object && this.object.getDecorationsCallback && this.object.getDecorationsCallback() || {};
			this.decorations =  JSON.parse(JSON.stringify(decos)) || {};
		}

		hasDecoration(name) {
			return this.decorations[name] !== undefined;
		}

		getDecoration(name) {
			return this.decorations[name];
		}

		/**
		 * decorate vipr data
		 * @param {String} name - name of decoration
		 * @param {Object} [value] - value of decoration
		 * @return the decoration value being saved in decorations.
		 */
		decorate(name, value) {
			/**
			 * TODO need to find a generic way to use decoration APIs post Endor R4
			 * Endor R4 solution - compare line stays as a special case to use the new VIDA decoration
			 */
			let decorationValue = value;
			if(name === 'lines'){
				decorationValue = this._createLinesDecoration(name, value);
			}
			if(name === TEXT_DECORATION.name) {
				decorationValue = this._decorateText(name, value);
			}
			// If this decoration isn't already in the map add it
			if (!(name in this.decorations) || (this.decorations[name] !== value)) {
				this.decorations[name] = decorationValue;
				this.context.version++;
			}
			return decorationValue;
		}

		clearDecorations() {
			let deleted = false;
			_.each(_.keys(this.decorations), name => {
				this.clearDecoration(name);
				deleted = true;
			});
			if (deleted) {
				this.context.version++;
			}
		}

		clearDecoration(name) {
			delete this.decorations[name];
		}

		resetDecorations() {
			this._initDecoratations();
			this.context.version++;
		}
		/**
		 *
		 * Create Text decoration which is used for hovering a legend/axis title/label.
		 * If the value is false, we need to remove the entire decoration since it's an object type decoration.
		 * Otherwise, set to default text decoration
		 * @param {boolean} value
		 * @returns
		 * @memberof VIPRDecoratable
		 */
		_decorateText(name, value) {
			if(value === false) {
				this.clearDecoration(name);
				return null;
			}
			const textDeco = this.getDecoration(name) || this._createTextDecoration();
			const { backgroundColor, font } = TEXT_DECORATION;
			textDeco.setBackground(backgroundColor);
			textDeco.setFont(font);
			return textDeco;
		}

		_createTextDecoration() {
			return new decorations.Text();
		}
		/**
		 *
		 * @param {String} name - decoration
		 * @param {(Array\|Object)} value - can be a spec object (when first creating the decoration) or an array of lines decoration (when re-apply the SAME decoration)
		 * example for spec:
		 * {
		 * 	baseLine: {
		 *		position: 100,
		 *		color: 'red'
		 *	},
		 *	grabber: {
		 *		positionType: 'END'
		 *	},
		 *	vane: {
		 *		caption: '3000.00'
		 *	}
		 *	}
		 */
		_createLinesDecoration(name, value) {
			const existingLinesDecorations = this.getDecoration(name);
			let linesDecorations = [];
			let baseLine;
			let grabber;
			let vane;
			_.each(existingLinesDecorations, (lineDecoration) => {
				if(lineDecoration instanceof decorations.BaseLine){
					baseLine = lineDecoration;
				} else if (lineDecoration instanceof decorations.Grabber) {
					grabber = lineDecoration;
				} else if (lineDecoration instanceof decorations.Vane) {
					vane = lineDecoration;
				}
			});
			// value can be a spec object (when first creating the decoration) or an array of lines decoration (when re-apply the SAME decoration)
			const isReDecorate = Array.isArray(value);
			if(isReDecorate){
				linesDecorations = value;
			} else if(value && !isReDecorate) {
				if(value.baseLine){
					baseLine = baseLine? baseLine: this._createBaseline('baseLine');
					if(typeof value.baseLine.position !== 'undefined'){
						baseLine.setPosition(value.baseLine.position);
					}
					if(value.baseLine.color){
						baseLine.setColor(value.baseLine.color);
					}
					baseLine.setShowInLegend(false || value.baseLine.setShowInLegend);
					linesDecorations.push(baseLine);
				}
				if(value.grabber && baseLine){
					baseLine.showGrabber(true);
					grabber = baseLine.getGrabber();
					grabber.setPositionType(value.grabber.positionType);
					linesDecorations.push(grabber);
				}
				// the label on the side of the compare line
				if(value.vane && baseLine) {
					baseLine.showVane(true);
					vane = baseLine.getVane();
					vane.setCaption(value.vane.caption);
					const transparentStroke = { r: 0, g: 0, b: 0, a: 0 };
					vane.setStrokeColor(transparentStroke);
					linesDecorations.push(vane);
				}
			}
			return linesDecorations;
		}

		_createBaseline(id) {
			const baseLineId = id || _.uniqueId('baseLine');
			return new decorations.BaseLine(baseLineId);
		}
	}

	return VIPRDecoratable;
});
